// BRAND NEW IMPLEMENTATION - Price calculation utilities for menu items

/**
 * Price calculation utility for menu items with modifiers
 * Provides reusable functions for calculating item totals
 */

export interface PriceModifier {
  id: string;
  name: string;
  price: string | number;
}

export interface PriceModifierGroup {
  id: string;
  name: string;
  options: PriceModifier[];
}

export interface SelectedModifier {
  quantity: number;
}

/**
 * Calculates the total price for a menu item with selected modifiers
 * 
 * @param baseItemPrice - The original price of the menu item
 * @param modifierGroups - Available modifier groups with their options
 * @param selectedModifiers - User's selected modifiers with quantities
 * @param orderQuantity - Number of items being ordered
 * @returns Total calculated price
 * 
 * @example
 * const total = calculateMenuItemTotalPrice(
 *   14.99,                    // Pizza base price
 *   modifierGroups,           // Available modifiers
 *   { 'size-id': { 'large-id': { quantity: 1 } } },
 *   2                         // Ordering 2 pizzas
 * );
 */
export function calculateMenuItemTotalPrice(
  baseItemPrice: number,
  modifierGroups: PriceModifierGroup[],
  selectedModifiers: Record<string, Record<string, SelectedModifier>>,
  orderQuantity: number = 1
): number {
  // Start with base price
  let pricePerItem = baseItemPrice;

  // Process each selected modifier group
  for (const [groupId, groupSelections] of Object.entries(selectedModifiers)) {
    // Find the modifier group
    const group = modifierGroups.find(g => g.id === groupId);
    if (!group) continue;

    // Process each selected option in this group
    for (const [optionId, selection] of Object.entries(groupSelections)) {
      // Find the specific option
      const option = group.options.find(opt => opt.id === optionId);
      if (!option) continue;

      // Parse price and add to total
      const modifierPrice = typeof option.price === 'number' 
        ? option.price 
        : parseFloat(String(option.price)) || 0;

      // Multiply by selection quantity (for multi-select modifiers)
      pricePerItem += modifierPrice * selection.quantity;
    }
  }

  // Multiply by order quantity
  return pricePerItem * orderQuantity;
}

